import { createApi } from '@reduxjs/toolkit/query/react'
import { BACKEND_API_URL } from '../consts/api'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import { getMediaSourceUrl } from '../utils/media'
import type {
  ICreatePlatformReportBody,
  IPlatformReportResource,
  IStaffPlatformReportResource,
  IUpdateStaffPlatformReportBody,
  PlatformReportStatus,
} from './types/platformReports'

const staffReportsTag = 'staffPlatformReports'

function normalizeStaffReport(raw: any): IStaffPlatformReportResource | null {
  const id = Number(raw?.id)
  if (!Number.isSafeInteger(id) || id <= 0) return null
  const reporterRaw = raw?.reporter
  const reviewedByRaw = raw?.reviewed_by
  return {
    id,
    target_type: raw?.target_type,
    target_id: Number(raw?.target_id),
    reason_code: raw?.reason_code,
    message: typeof raw?.message === 'string' ? raw.message : null,
    status: raw?.status,
    created_at: String(raw?.created_at ?? ''),
    staff_note: typeof raw?.staff_note === 'string' ? raw.staff_note : null,
    reviewed_at: raw?.reviewed_at != null ? String(raw.reviewed_at) : null,
    reporter: reporterRaw
      ? {
          id: reporterRaw.id ?? null,
          slug: reporterRaw.slug ?? null,
          shortname: reporterRaw.shortname ?? null,
          fullname: reporterRaw.fullname ?? null,
          avatar: getMediaSourceUrl(reporterRaw.avatar),
          deleted: reporterRaw.deleted === true,
        }
      : null,
    reviewed_by: reviewedByRaw
      ? {
          id: reviewedByRaw.id ?? null,
          slug: reviewedByRaw.slug ?? null,
          shortname: reviewedByRaw.shortname ?? null,
          fullname: reviewedByRaw.fullname ?? null,
        }
      : null,
  }
}

export const platformReportsApi = createApi({
  reducerPath: 'platformReportsApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [staffReportsTag],
  endpoints: (builder) => ({
    createPlatformReport: builder.mutation<IPlatformReportResource, ICreatePlatformReportBody>({
      query: (body) => ({
        url: '/platform-reports',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown): IPlatformReportResource => {
        const item = extractResponseData(response) as any
        return {
          id: Number(item?.id),
          target_type: item?.target_type,
          target_id: Number(item?.target_id),
          reason_code: item?.reason_code,
          message: typeof item?.message === 'string' ? item.message : null,
          status: item?.status,
          created_at: String(item?.created_at ?? ''),
        }
      },
      transformErrorResponse: extractResponseError,
    }),

    getStaffPlatformReports: builder.query<
      { items: IStaffPlatformReportResource[]; nextCursor: string | null },
      { status?: PlatformReportStatus; cursor?: string | null; limit?: number }
    >({
      query: ({ status = 'open', cursor, limit = 50 }) => ({
        url: '/staff/platform-reports',
        params: {
          status,
          ...(cursor ? { cursor } : {}),
          limit,
        },
      }),
      transformResponse: (response: unknown) => {
        const payload = response as { data?: unknown; meta?: { next_cursor?: string | null } }
        const list = Array.isArray(payload?.data) ? payload.data : []
        const items = list
          .map((item) => normalizeStaffReport(item))
          .filter((item): item is IStaffPlatformReportResource => item !== null)
        return {
          items,
          nextCursor: payload?.meta?.next_cursor ?? null,
        }
      },
      transformErrorResponse: extractResponseError,
      providesTags: [staffReportsTag],
    }),

    updateStaffPlatformReport: builder.mutation<
      IStaffPlatformReportResource,
      { reportId: number; body: IUpdateStaffPlatformReportBody }
    >({
      query: ({ reportId, body }) => ({
        url: `/staff/platform-reports/${reportId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => {
        const item = extractResponseData(response) as any
        const normalized = normalizeStaffReport(item)
        if (!normalized) {
          throw new Error('Invalid staff report response')
        }
        return normalized
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: [staffReportsTag],
    }),
  }),
})

export const {
  useCreatePlatformReportMutation,
  useGetStaffPlatformReportsQuery,
  useLazyGetStaffPlatformReportsQuery,
  useUpdateStaffPlatformReportMutation,
} = platformReportsApi
